import {
  getLicensePool,
  getSystemLicenseClass,
  type LicensePool,
} from '../config/licensepools.config';

type LicenseDevice = {
  id?: string | number;
  system_type?: string | null;
  device_type?: string | null;
};

type LicenseInfo = {
  connected_cameras?: number | null;
  remaining_connected_cameras?: number | null;
  connected_devices?: number | null;
  remaining_connected_devices?: number | null;
  [key: string]: number | string | null | undefined;
};

type LicenseTotals = {
  limit: number | null | undefined;
  remaining: number | null | undefined;
};

type LicenseSystem = {
  id?: string | number;
  system_id?: string | number;
  system_type?: string | null;
  licenseDeviceTypes?: string[];
};

type LicenseSystemUsageCounts = Map<string, Set<string>>;
const ADVANCED_SYSTEM_LICENSE_KEY = 'advanced_system';

/**
 * Groups the device types present in the workplace by the system type they belong to.
 *
 * @param connectedDevices - The unfiltered connected device list.
 * @returns A map of system type to the distinct device types recorded against it.
 */
export const getDeviceTypesBySystemType = (
  connectedDevices: LicenseDevice[] = [],
): Map<string, string[]> => {
  const deviceTypesBySystemType = new Map<string, Set<string>>();

  connectedDevices.forEach((device) => {
    if (!device.system_type || !device.device_type) return;

    if (!deviceTypesBySystemType.has(device.system_type)) {
      deviceTypesBySystemType.set(device.system_type, new Set());
    }

    deviceTypesBySystemType.get(device.system_type)?.add(device.device_type);
  });

  return new Map(
    [...deviceTypesBySystemType].map(([systemType, deviceTypes]) => [systemType, [...deviceTypes]]),
  );
};

/**
 * Pairs every integration with the device types that decide its license class.
 *
 * Built from the raw integration and device lists rather than the Connected Systems
 * hierarchy, which is filtered by the search box and by per-device-type view permissions.
 * Deriving license indexes from the filtered hierarchy makes them renumber as the user
 * searches, and differ between users with different device permissions.
 *
 * Attribution is per system *type*, not per system instance: a connected device records the
 * system_type it belongs to but carries no id for the specific integration, so two RescAlert
 * systems in the same workplace always receive the same device type list and therefore the
 * same license class. Building the hierarchy makes the same assumption - getMatchingDevices
 * in systemStructure.hierarchy.js matches on system_type and device_type only.
 *
 * @param integrations - The unfiltered integration list.
 * @param connectedDevices - The unfiltered connected device list.
 * @returns Each integration with its license-relevant device types attached.
 */
export const getLicenseSystems = (
  integrations: LicenseSystem[] = [],
  connectedDevices: LicenseDevice[] = [],
): LicenseSystem[] => {
  const deviceTypesBySystemType = getDeviceTypesBySystemType(connectedDevices);

  return integrations.map((system) => ({
    ...system,
    licenseDeviceTypes: deviceTypesBySystemType.get(system.system_type ?? '') || [],
  }));
};

export const getDeviceIndexMap = (
  connectedDevices: LicenseDevice[] = [],
): Map<string | number, number> => {
  const map = new Map<string | number, number>();
  const poolCounts: Partial<Record<LicensePool, number>> = {};

  connectedDevices.forEach((device) => {
    const pool = getLicensePool(device.system_type, device.device_type);
    if (pool === 'virtual' || device.id === undefined) return;

    poolCounts[pool] = (poolCounts[pool] || 0) + 1;
    map.set(device.id, poolCounts[pool] as number);
  });

  return map;
};

export const getDeviceLicenseInfo = (
  device: LicenseDevice,
  licenseInfo: LicenseInfo = {},
): LicenseTotals | null => {
  const pool = getLicensePool(device?.system_type, device?.device_type);
  if (pool === 'virtual') return null;

  const isCameraPool = pool === 'camera';
  return {
    limit: isCameraPool ? licenseInfo.connected_cameras : licenseInfo.connected_devices,
    remaining: isCameraPool
      ? licenseInfo.remaining_connected_cameras
      : licenseInfo.remaining_connected_devices,
  };
};

export const getDeviceLicenseLabel = (
  device: LicenseDevice,
  licenseInfo: LicenseInfo = {},
  deviceIndexMap: Map<string | number, number>,
): string => {
  const license = getDeviceLicenseInfo(device, licenseInfo);
  if (!license || device.id === undefined) return '';

  const pool = getLicensePool(device.system_type, device.device_type);
  const poolLabel = pool === 'camera' ? 'Camera' : 'Device';
  return `${deviceIndexMap.get(device.id)}/${license.limit} (${poolLabel})`;
};

const getSystemKey = (system: LicenseSystem = {}): string =>
  `${system.id ?? system.system_id ?? system.system_type ?? 'unknown'}::${system.system_type ?? ''}`;

export const getAdvancedSystemLicenseLimit = (licenseInfo: LicenseInfo = {}): number =>
  Number(licenseInfo[ADVANCED_SYSTEM_LICENSE_KEY]) || 0;

export const getSystemTypeUsageCounts = (
  integrations: LicenseSystem[] = [],
): LicenseSystemUsageCounts => {
  const systemTypeMap: LicenseSystemUsageCounts = new Map();

  integrations.forEach((system) => {
    if (
      !system.system_type ||
      getSystemLicenseClass(system.system_type, system.licenseDeviceTypes) === 'basic'
    ) {
      return;
    }

    if (!systemTypeMap.has(ADVANCED_SYSTEM_LICENSE_KEY)) {
      systemTypeMap.set(ADVANCED_SYSTEM_LICENSE_KEY, new Set());
    }

    systemTypeMap.get(ADVANCED_SYSTEM_LICENSE_KEY)?.add(getSystemKey(system));
  });

  return systemTypeMap;
};

export const getAdvancedSystemUsageCount = (
  systemTypeUsageCounts: LicenseSystemUsageCounts | undefined,
): number => systemTypeUsageCounts?.get(ADVANCED_SYSTEM_LICENSE_KEY)?.size || 0;

export const getSystemIndexMap = (systems: LicenseSystem[] = []): Map<string | number, number> => {
  const map = new Map<string | number, number>();
  const countedSystemKeys = new Set<string | number>();
  let advancedSystemIndex = 0;

  systems.forEach((system) => {
    const systemKey = system.id ?? system.system_id;
    if (
      !system.system_type ||
      systemKey === undefined ||
      getSystemLicenseClass(system.system_type, system.licenseDeviceTypes) === 'basic'
    ) {
      return;
    }
    if (countedSystemKeys.has(systemKey)) return;

    countedSystemKeys.add(systemKey);
    advancedSystemIndex += 1;
    map.set(systemKey, advancedSystemIndex);
  });

  return map;
};

export const getSystemLicenseLabel = (
  system: LicenseSystem,
  licenseInfo: LicenseInfo = {},
  systemIndexMap: Map<string | number, number>,
): string => {
  const systemKey = system.id ?? system.system_id;
  if (systemKey === undefined) return '';

  const systemIndex = systemIndexMap?.get(systemKey);
  if (systemIndex === undefined) return '';

  return `${systemIndex}/${getAdvancedSystemLicenseLimit(licenseInfo)} (System)`;
};
